import { motion } from "framer-motion";
import {
    KanbanSquare,
    MessageSquare,
    FileText,
    PenTool,
    Building2,
    Receipt,
    UserCog,
    Timer,
    Workflow,
    BarChart3,
    Inbox,
    Table2,
    ArrowRight,
} from "lucide-react";

const categories = [
    {
        icon: KanbanSquare,
        title: "Projects & Tasks",
        sub: "Kanban, sprints, backlogs, Gantt & roadmaps.",
        module: "Projects",
        accent: "text-electric",
        ring: "ring-electric/30",
        replaces: ["Jira", "ClickUp", "Linear", "Asana", "YouTrack", "Trello", "Monday"],
    },
    {
        icon: MessageSquare,
        title: "Team Communication",
        sub: "Channels, threads, DMs, video & voice.",
        module: "Chat",
        accent: "text-violet-400",
        ring: "ring-violet-400/30",
        replaces: ["Slack", "Discord", "MS Teams", "Mattermost", "Rocket.Chat"],
    },
    {
        icon: FileText,
        title: "Docs & Knowledge",
        sub: "Wikis, notes, runbooks, SOPs.",
        module: "Docs",
        accent: "text-amber-glow",
        ring: "ring-amber-glow/30",
        replaces: ["Notion", "Confluence", "Coda", "Google Docs", "Slab"],
    },
    {
        icon: PenTool,
        title: "Whiteboards & Mind Maps",
        sub: "Visual planning & async ideation.",
        module: "Whiteboards",
        accent: "text-rose-400",
        ring: "ring-rose-400/30",
        replaces: ["Miro", "FigJam", "Mural", "Lucidchart", "Whimsical"],
    },
    {
        icon: Building2,
        title: "CRM & Sales",
        sub: "Leads, pipelines, deals, forecasts.",
        module: "CRM",
        accent: "text-emerald-400",
        ring: "ring-emerald-400/30",
        replaces: ["Salesforce", "HubSpot", "Zoho CRM", "Pipedrive", "Close"],
    },
    {
        icon: Receipt,
        title: "Invoicing & Accounting",
        sub: "Invoices, expenses, books & reports.",
        module: "Finance",
        accent: "text-emerald-300",
        ring: "ring-emerald-300/30",
        replaces: ["QuickBooks", "Xero", "Zoho Invoice", "FreshBooks", "Wave"],
    },
    {
        icon: UserCog,
        title: "HR & People",
        sub: "Hiring, payroll, attendance, reviews.",
        module: "HR",
        accent: "text-indigo-300",
        ring: "ring-indigo-300/30",
        replaces: ["BambooHR", "Workday", "Gusto", "Rippling", "Deel"],
    },
    {
        icon: Timer,
        title: "Time Tracking",
        sub: "Timesheets, billable hours, capacity.",
        module: "Time",
        accent: "text-amber-glow",
        ring: "ring-amber-glow/30",
        replaces: ["Toggl", "Harvest", "Clockify", "Hubstaff", "RescueTime"],
    },
    {
        icon: Workflow,
        title: "Attendance & Payroll",
        sub: "Attendance, leave, payroll inputs & performance — all in one record.",
        module: "HR",
        accent: "text-indigo-300",
        ring: "ring-indigo-300/30",
        replaces: ["BambooHR", "Workday", "Gusto", "Rippling", "Deel"],
    },
    {
        icon: BarChart3,
        title: "Analytics & Dashboards",
        sub: "BI on live operational data.",
        module: "Dashboards",
        accent: "text-electric",
        ring: "ring-electric/30",
        replaces: ["Tableau", "Looker", "Metabase", "Mode", "PowerBI"],
    },
    {
        icon: Inbox,
        title: "Shared Inbox & Support",
        sub: "Email triage, helpdesk, SLAs.",
        module: "Inbox",
        accent: "text-rose-300",
        ring: "ring-rose-300/30",
        replaces: ["Front", "Help Scout", "Intercom", "Zendesk", "Freshdesk"],
    },
    {
        icon: Table2,
        title: "Spreadsheets & Sheets",
        sub: "Structured data with views & formulas.",
        module: "Spreadsheets",
        accent: "text-emerald-300",
        ring: "ring-emerald-300/30",
        replaces: ["Airtable", "Smartsheet", "Google Sheets", "Excel", "Stackby"],
    },
];

const CompetitorPill = ({ name }) => (
    <span className="inline-flex items-center font-code text-[10.5px] sm:text-[11px] text-zinc-500 line-through decoration-zinc-700 decoration-[1.5px] px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.05] hover:text-zinc-300 hover:decoration-zinc-500 transition">
        {name}
    </span>
);

const Card = ({ cat, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, delay: Math.min(index * 0.05, 0.4) }}
        whileHover={{ y: -3 }}
        className="group relative bg-obsidian-200 hover:bg-obsidian-100 border border-white/[0.06] hover:border-white/15 rounded-2xl p-6 sm:p-7 transition-colors duration-300"
        data-testid={`replaces-card-${cat.module.toLowerCase()}`}
    >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
                <span
                    className={`w-9 h-9 rounded-lg flex items-center justify-center bg-white/[0.04] ring-1 ${cat.ring}`}
                >
                    <cat.icon className={`w-4 h-4 ${cat.accent}`} strokeWidth={1.6} />
                </span>
                <div>
                    <h3 className="font-display text-base sm:text-lg font-semibold text-white tracking-tight leading-tight">
                        {cat.title}
                    </h3>
                </div>
            </div>
            <span className="font-code text-[9px] uppercase tracking-[0.2em] text-zinc-600 mt-2">
                0{index + 1}
            </span>
        </div>

        <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed font-light mb-5">
            {cat.sub}
        </p>

        {/* Replaces label */}
        <div className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-3">
            {"// you stop paying for"}
        </div>

        {/* Strikethrough competitor pills */}
        <div className="flex flex-wrap gap-1.5 mb-6">
            {cat.replaces.map((r) => (
                <CompetitorPill key={r} name={r} />
            ))}
        </div>

        {/* Replacement */}
        <div className="flex items-center gap-2 pt-4 border-t border-white/[0.06]">
            <ArrowRight className={`w-3.5 h-3.5 ${cat.accent}`} />
            <span className="font-code text-[11px] text-zinc-500">
                Now in
            </span>
            <span
                className={`font-code text-[11px] font-semibold ${cat.accent} px-2 py-0.5 rounded bg-white/[0.03] border border-white/[0.08]`}
            >
                ApexBrain.{cat.module}
            </span>
        </div>
    </motion.div>
);

export const Replaces = () => {
    const totalReplaces = categories.reduce(
        (acc, c) => acc + c.replaces.length,
        0,
    );

    return (
        <section
            id="replaces"
            className="relative py-24 sm:py-32 overflow-hidden"
            data-testid="replaces-section"
        >
            <div className="absolute inset-0 grid-bg opacity-[0.25] pointer-events-none" />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                            $ uninstall --all
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                            Cancel {totalReplaces} subscriptions.
                            <br />
                            <span className="text-zinc-500">
                                Keep one workspace.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:pb-2">
                        <p className="text-zinc-400 font-light text-sm sm:text-base leading-relaxed">
                            ApexBrain replaces the long list of tools your team
                            duct‑tapes together. Same data, same identity, same
                            search — across every function of your company.
                        </p>
                        <div className="mt-5 flex items-center gap-4 font-code text-[11px] text-zinc-500">
                            <span className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                <span className="line-through decoration-zinc-600">
                                    {totalReplaces}+ tools
                                </span>
                            </span>
                            <span className="text-zinc-700">→</span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                                <span className="text-emerald-300">
                                    1 workspace
                                </span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Cards grid */}
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                    data-testid="replaces-grid"
                >
                    {categories.map((c, i) => (
                        <Card key={c.module} cat={c} index={i} />
                    ))}
                </div>

                {/* Footer note */}
                <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-code text-zinc-500">
                    <span>
                        <span className="text-zinc-600">{"// estimated"}</span>{" "}
                        avg savings:{" "}
                        <span className="text-emerald-300 font-semibold">
                            $14,400 / yr
                        </span>{" "}
                        per 20‑seat team
                    </span>
                    <a
                        href="/pricing"
                        className="text-zinc-400 hover:text-white transition font-semibold inline-flex items-center gap-1"
                    >
                        See total savings →
                    </a>
                </div>
            </div>
        </section>
    );
};
